import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router";
import { Plus, Trash2, AlertCircle, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SpinnerCenter } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listFeaturedRewards, deleteFeaturedReward, reorderFeaturedRewards } from "@/lib/api/admin";
import type { FeaturedReward } from "@/lib/api/types";
import { TAB_POINT_REWARDS, TAB_STAMP_REWARDS } from "@/lib/utils/storeTabs";
import { AddFeaturedRewardDialog } from "./stores/components/AddFeaturedRewardDialog";

function requirementText(r: FeaturedReward): string {
  return r.reward_type === "point"
    ? `${(r.points_required ?? 0).toLocaleString()} pts`
    : `${r.stamps_required ?? 0} stamps`;
}

function rewardLink(r: FeaturedReward): string {
  const tab = r.reward_type === "point" ? TAB_POINT_REWARDS : TAB_STAMP_REWARDS;
  return `/stores/${r.store_id}?tab=${tab}&rewardId=${r.reward_id}`;
}

export function FeaturedRewardsPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState<FeaturedReward | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const {
    data: rewards = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["featured-rewards"],
    queryFn: () => listFeaturedRewards(user!),
    enabled: !!user,
  });

  // Sync only when the ID set changes (anti-jitter, per CategoriesEditor); seeded from `rewards` so cached data at mount isn't dropped.
  const rewardsKey = [...rewards]
    .map((r) => r.id)
    .sort()
    .join(",");
  const [ordered, setOrdered] = useState<FeaturedReward[]>(rewards);
  const [prevRewardsKey, setPrevRewardsKey] = useState(rewardsKey);
  if (rewardsKey !== prevRewardsKey) {
    setPrevRewardsKey(rewardsKey);
    setOrdered(rewards);
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFeaturedReward(user!, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["featured-rewards"] });
      setToDelete(null);
      toast.success("Featured reward removed");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderFeaturedRewards(user!, ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["featured-rewards"] }),
    onError: () => {
      setOrdered(rewards);
      toast.error("Failed to reorder featured rewards.");
    },
  });

  function handleDragStart(index: number) {
    setDragIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    setOverIndex(index);
  }

  function handleDrop(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setOrdered(next);
    setDragIndex(null);
    setOverIndex(null);
    reorderMutation.mutate(next.map((r) => r.id));
  }

  function handleDragEnd() {
    setDragIndex(null);
    setOverIndex(null);
  }

  if (isLoading) return <SpinnerCenter />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load featured rewards</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Featured Rewards</h1>
          <p className="text-sm text-muted-foreground">
            Curated rewards shown in the customer app&apos;s home page carousel.
          </p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="size-3.5 mr-1" /> Add Featured Reward
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Featured ({ordered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {ordered.length === 0 ? (
            <EmptyState
              title="No featured rewards"
              description='Add one with "Add Featured Reward" above.'
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-2">Drag to reorder.</p>
              <ul className="divide-y rounded-md border">
                {ordered.map((r, i) => (
                  <li
                    key={r.id}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDrop={(e) => handleDrop(e, i)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-start gap-2 p-3 transition-colors ${
                      overIndex === i ? "bg-primary/5" : ""
                    }`}
                  >
                    <GripVertical className="size-4 text-muted-foreground cursor-grab shrink-0 mt-1.5" />
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={rewardLink(r)}
                          className="font-medium text-sm text-primary hover:underline"
                        >
                          {r.name}
                        </Link>
                        <Badge variant="outline">
                          {r.reward_type === "point" ? "Points" : "Stamps"}
                        </Badge>
                        {!r.is_available && (
                          <Badge
                            variant="outline"
                            className="bg-amber-100 text-amber-800 border-amber-200"
                          >
                            Unavailable
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {r.store_name} · {requirementText(r)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10 shrink-0"
                      onClick={() => setToDelete(r)}
                      aria-label={`Remove ${r.name}`}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      <AddFeaturedRewardDialog open={addOpen} onOpenChange={setAddOpen} />

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Remove featured reward?"
        description={`"${toDelete?.name}" will no longer appear in the customer app's home carousel.`}
        confirmLabel="Remove"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.id)}
      />
    </div>
  );
}
